import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API, auth, inputCls, Field, Modal, PrimaryBtn, errMsg, fmtDate, exportPdf } from './finance/shared';
import { EmptyState } from '../../components/dashboard/TodaySchedule';
import Icon from '../../components/dashboard/DashboardIcons';
import { getReportSettings, logExport } from '../../lib/reportStudioApi';
import { useClubIdentity } from '../../lib/useClubIdentity';
import ReportPrintFrame from '../../components/reportStudio/ReportPrintFrame';

export const MEETING_TYPES = { board: 'مجلس إدارة', general_assembly: 'جمعية عمومية', committee: 'لجنة', technical: 'فني', administrative: 'إداري', financial: 'مالي', general: 'عام', emergency: 'طارئ', other: 'أخرى' };
const MEETING_STATUS_LABELS = { draft: 'مسودة', approved: 'معتمد', archived: 'مؤرشف' };
const MEETING_STATUS_CLS = { draft: 'bg-amber-50 text-amber-700', approved: 'bg-teal-50 text-teal-700', archived: 'bg-slate-100 text-slate-500' };
const DECISION_STATUS_LABELS = { not_started: 'لم يبدأ', in_progress: 'قيد التنفيذ', completed: 'منفذ', postponed: 'مؤجل', cancelled: 'ملغي' };
const DECISION_STATUS_CLS = { not_started: 'bg-slate-100 text-slate-600', in_progress: 'bg-blue-50 text-blue-700', completed: 'bg-teal-50 text-teal-700', postponed: 'bg-amber-50 text-amber-700', cancelled: 'bg-red-50 text-red-600' };

const MEETING_DETAIL_TABS = [
  { key: 'minutes', label: 'المحضر' },
  { key: 'decisions', label: 'القرارات' },
  { key: 'attachments', label: 'المرفقات' },
  { key: 'activity', label: 'سجل النشاط' },
];
const STAT_COLORS = {
  slate: { icon: 'text-slate-500', value: 'text-slate-900', bg: 'bg-slate-100' },
  teal: { icon: 'text-teal-600', value: 'text-teal-700', bg: 'bg-teal-50' },
  blue: { icon: 'text-blue-600', value: 'text-blue-700', bg: 'bg-blue-50' },
  red: { icon: 'text-red-600', value: 'text-red-600', bg: 'bg-red-50' },
};
function DecisionSummaryStat({ icon, color, label, value, testId }) {
  const c = STAT_COLORS[color] || STAT_COLORS.slate;
  return (
    <div className="flex items-center gap-2.5 rounded-lg border border-slate-100 p-2.5" data-testid={testId}>
      <span className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${c.bg}`}><Icon name={icon} className={`h-4 w-4 ${c.icon}`} /></span>
      <div className="min-w-0">
        <p className={`text-base font-bold leading-tight ${c.value}`}>{value}</p>
        <p className="truncate text-[11px] text-slate-500">{label}</p>
      </div>
    </div>
  );
}

const getUser = () => { try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; } };
const getUserName = () => { try { return JSON.parse(localStorage.getItem('user') || 'null')?.full_name; } catch { return null; } };
const linesToArr = (s) => (s || '').split('\n').map((x) => x.trim()).filter(Boolean);
const emptyForm = () => ({ title: '', meeting_type: 'general', meeting_date: '', meeting_time: '', location: '', attendees: '', apologies: '', summary: '', agenda: [''], previous_meeting_id: '', next_meeting_id: '', decisions: [] });

function MeetingFields({ form, setForm, meetings, excludeId }) {
  const addAgenda = () => setForm((f) => ({ ...f, agenda: [...(f.agenda || ['']), ''] }));
  const updAgenda = (i, v) => setForm((f) => ({ ...f, agenda: (f.agenda || ['']).map((x, idx) => (idx === i ? v : x)) }));
  const rmAgenda = (i) => setForm((f) => ({ ...f, agenda: (f.agenda || ['']).filter((_, idx) => idx !== i) }));
  const addDecision = () => setForm((f) => ({ ...f, decisions: [...(f.decisions || []), { text: '', responsible: '', due_date: '', status: 'not_started' }] }));
  const updDecision = (i, k, v) => setForm((f) => ({ ...f, decisions: f.decisions.map((d, idx) => (idx === i ? { ...d, [k]: v } : d)) }));
  const rmDecision = (i) => setForm((f) => ({ ...f, decisions: f.decisions.filter((_, idx) => idx !== i) }));
  const linkOptions = (meetings || []).filter((m) => m.id !== excludeId);

  return (
    <>
      <Field label="العنوان" required><input className={inputCls} value={form.title || ''} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="meeting-input-title" /></Field>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Field label="نوع الاجتماع" required>
          <select className={inputCls} value={form.meeting_type || 'general'} onChange={(e) => setForm({ ...form, meeting_type: e.target.value })} data-testid="meeting-input-type">
            {Object.entries(MEETING_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </Field>
        <Field label="تاريخ الاجتماع" required><input type="date" className={inputCls} value={form.meeting_date || ''} onChange={(e) => setForm({ ...form, meeting_date: e.target.value })} data-testid="meeting-input-date" /></Field>
        <Field label="الوقت"><input type="time" className={inputCls} value={form.meeting_time || ''} onChange={(e) => setForm({ ...form, meeting_time: e.target.value })} data-testid="meeting-input-time" /></Field>
      </div>
      <Field label="المكان"><input className={inputCls} value={form.location || ''} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="meeting-input-location" /></Field>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="الحاضرون (اسم في كل سطر)"><textarea rows={3} className={inputCls} value={form.attendees || ''} onChange={(e) => setForm({ ...form, attendees: e.target.value })} data-testid="meeting-input-attendees" /></Field>
        <Field label="المعتذرون (اسم في كل سطر)"><textarea rows={3} className={inputCls} value={form.apologies || ''} onChange={(e) => setForm({ ...form, apologies: e.target.value })} data-testid="meeting-input-apologies" /></Field>
      </div>
      <Field label="جدول الأعمال">
        <div className="space-y-2">
          {(form.agenda || ['']).map((a, i) => (
            <div key={i} className="flex items-center gap-2">
              <input className={inputCls} placeholder={`البند ${i + 1}`} value={a} onChange={(e) => updAgenda(i, e.target.value)} data-testid={`meeting-agenda-input-${i}`} />
              {(form.agenda || ['']).length > 1 && <button type="button" onClick={() => rmAgenda(i)} className="rounded-lg p-2 text-slate-400 hover:bg-red-50 hover:text-red-600" data-testid={`meeting-agenda-remove-${i}`}><Icon name="trash" className="w-4 h-4" /></button>}
            </div>
          ))}
          <button type="button" onClick={addAgenda} className="text-xs font-semibold text-teal-700 hover:text-teal-800" data-testid="meeting-agenda-add">+ إضافة بند</button>
        </div>
      </Field>
      <Field label="ملخص الاجتماع / المحضر"><textarea rows={4} className={inputCls} value={form.summary || ''} onChange={(e) => setForm({ ...form, summary: e.target.value })} data-testid="meeting-input-summary" /></Field>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="الاجتماع السابق (ربط)">
          <select className={inputCls} value={form.previous_meeting_id || ''} onChange={(e) => setForm({ ...form, previous_meeting_id: e.target.value })} data-testid="meeting-input-previous">
            <option value="">— بلا ربط —</option>
            {linkOptions.map((m) => <option key={m.id} value={m.id}>{m.meeting_number} — {m.title}</option>)}
          </select>
        </Field>
        <Field label="الاجتماع اللاحق (ربط)">
          <select className={inputCls} value={form.next_meeting_id || ''} onChange={(e) => setForm({ ...form, next_meeting_id: e.target.value })} data-testid="meeting-input-next">
            <option value="">— بلا ربط —</option>
            {linkOptions.map((m) => <option key={m.id} value={m.id}>{m.meeting_number} — {m.title}</option>)}
          </select>
        </Field>
      </div>
      {form.decisions !== undefined && (
        <Field label="القرارات (اختياري)">
          <div className="space-y-2">
            {(form.decisions || []).map((d, i) => (
              <div key={i} className="rounded-lg border border-slate-200 p-2 space-y-2" data-testid={`meeting-decision-row-${i}`}>
                <div className="flex items-center gap-2">
                  <input className={inputCls} placeholder="نص القرار" value={d.text} onChange={(e) => updDecision(i, 'text', e.target.value)} data-testid={`meeting-decision-text-${i}`} />
                  <button type="button" onClick={() => rmDecision(i)} className="rounded-lg p-2 text-slate-400 hover:bg-red-50 hover:text-red-600" data-testid={`meeting-decision-remove-${i}`}><Icon name="trash" className="w-4 h-4" /></button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input className={inputCls} placeholder="المسؤول" value={d.responsible} onChange={(e) => updDecision(i, 'responsible', e.target.value)} data-testid={`meeting-decision-responsible-${i}`} />
                  <input type="date" className={inputCls} value={d.due_date} onChange={(e) => updDecision(i, 'due_date', e.target.value)} data-testid={`meeting-decision-due-${i}`} />
                </div>
              </div>
            ))}
            <button type="button" onClick={addDecision} className="text-xs font-semibold text-teal-700 hover:text-teal-800" data-testid="meeting-decision-add">+ إضافة قرار</button>
          </div>
        </Field>
      )}
    </>
  );
}

export default function MeetingsTab() {
  const user = getUser();
  const canManage = !!(user?.is_super_admin || user?.role === 'admin' || user?.role === 'manager');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ meeting_type: '', status: '', search: '', date_from: '', date_to: '' });
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [formModal, setFormModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [amendModal, setAmendModal] = useState(false);
  const [amendForm, setAmendForm] = useState({});
  const [amendReason, setAmendReason] = useState('');
  const [confirmDel, setConfirmDel] = useState(false);
  const [err, setErr] = useState(null);
  const [toast, setToast] = useState(null);
  const [pdfExporting, setPdfExporting] = useState(false);
  const [newDecision, setNewDecision] = useState({ text: '', responsible: '', due_date: '' });
  const [attachFile, setAttachFile] = useState(null);
  const [attachReason, setAttachReason] = useState('');
  const [activeTab, setActiveTab] = useState('minutes');
  const [meetingActivity, setMeetingActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const printRef = useRef(null);
  const [studio, setStudio] = useState(null);
  const identity = useClubIdentity();
  const notify = (m) => { setToast(m); setTimeout(() => setToast(null), 3000); };

  useEffect(() => { getReportSettings('meeting_minutes').then(setStudio).catch(() => setStudio(null)); }, []);
  useEffect(() => { setActiveTab('minutes'); }, [selectedId]);
  useEffect(() => {
    if (activeTab !== 'activity' || !selectedId) return;
    setActivityLoading(true);
    axios.get(`${API}/meetings/${selectedId}/activity`, auth())
      .then((res) => setMeetingActivity(Array.isArray(res.data) ? res.data : []))
      .catch(() => setMeetingActivity([]))
      .finally(() => setActivityLoading(false));
  }, [activeTab, selectedId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await axios.get(`${API}/meetings`, { ...auth(), params });
      setList(Array.isArray(res.data) ? res.data : []);
    } catch { setList([]); }
    setLoading(false);
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const loadDetail = useCallback(async (id) => {
    if (!id) { setDetail(null); return; }
    setDetailLoading(true);
    try { const res = await axios.get(`${API}/meetings/${id}`, auth()); setDetail(res.data); }
    catch (e) { notify(errMsg(e, 'تعذر تحميل الاجتماع')); setDetail(null); }
    setDetailLoading(false);
  }, []);
  useEffect(() => { loadDetail(selectedId); }, [selectedId, loadDetail]);

  const buildPayload = (f) => ({
    title: f.title?.trim(),
    meeting_type: f.meeting_type,
    meeting_date: f.meeting_date,
    meeting_time: f.meeting_time || null,
    location: f.location || null,
    attendees: linesToArr(f.attendees),
    apologies: linesToArr(f.apologies),
    agenda: (f.agenda || []).map((a) => a.trim()).filter(Boolean),
    summary: f.summary || null,
    previous_meeting_id: f.previous_meeting_id || null,
    next_meeting_id: f.next_meeting_id || null,
  });

  const openCreate = () => { setEditing(null); setForm(emptyForm()); setErr(null); setFormModal(true); };
  const openEdit = (m) => {
    setEditing(m);
    setForm({ ...m, attendees: (m.attendees || []).join('\n'), apologies: (m.apologies || []).join('\n'), agenda: m.agenda && m.agenda.length ? m.agenda : [''], meeting_date: String(m.meeting_date || '').slice(0, 10), decisions: undefined, previous_meeting_id: m.previous_meeting_id || '', next_meeting_id: m.next_meeting_id || '' });
    setErr(null); setFormModal(true);
  };

  const submitForm = async (e) => {
    e.preventDefault(); setErr(null);
    if (!form.title?.trim()) { setErr('العنوان مطلوب'); return; }
    if (!form.meeting_date) { setErr('تاريخ الاجتماع مطلوب'); return; }
    try {
      if (editing) {
        await axios.put(`${API}/meetings/${editing.id}`, buildPayload(form), auth());
      } else {
        const payload = { ...buildPayload(form), decisions: (form.decisions || []).filter((d) => d.text?.trim()).map((d) => ({ text: d.text.trim(), responsible: d.responsible || null, due_date: d.due_date || null, status: 'not_started' })) };
        await axios.post(`${API}/meetings`, payload, auth());
      }
      setFormModal(false); notify('تم الحفظ بنجاح'); load();
      if (editing) loadDetail(editing.id);
    } catch (e2) { setErr(errMsg(e2, 'فشل الحفظ')); }
  };

  const openAmend = () => {
    setAmendForm({ ...detail, attendees: (detail.attendees || []).join('\n'), apologies: (detail.apologies || []).join('\n'), agenda: detail.agenda && detail.agenda.length ? detail.agenda : [''], meeting_date: String(detail.meeting_date || '').slice(0, 10), previous_meeting_id: detail.previous_meeting_id || '', next_meeting_id: detail.next_meeting_id || '' });
    setAmendReason(''); setErr(null); setAmendModal(true);
  };
  const submitAmend = async (e) => {
    e.preventDefault(); setErr(null);
    if (!amendReason.trim() || amendReason.trim().length < 3) { setErr('سبب التعديل مطلوب (3 أحرف على الأقل)'); return; }
    try {
      await axios.post(`${API}/meetings/${detail.id}/amend`, { ...buildPayload(amendForm), reason: amendReason.trim() }, auth());
      setAmendModal(false); notify('تم حفظ التعديل الاستثنائي'); load(); loadDetail(detail.id);
    } catch (e2) { setErr(errMsg(e2, 'فشل التعديل')); }
  };

  const doApprove = async () => { try { await axios.post(`${API}/meetings/${detail.id}/approve`, {}, auth()); notify('تم اعتماد المحضر'); load(); loadDetail(detail.id); } catch (e2) { notify(errMsg(e2, 'فشل الاعتماد')); } };
  const doArchive = async () => { try { await axios.post(`${API}/meetings/${detail.id}/archive`, {}, auth()); notify('تمت الأرشفة'); load(); loadDetail(detail.id); } catch (e2) { notify(errMsg(e2, 'فشل الأرشفة')); } };
  const doDelete = async () => {
    try { await axios.delete(`${API}/meetings/${confirmDel.id}`, auth()); notify('تم حذف المسودة'); setConfirmDel(null); setSelectedId(null); setDetail(null); load(); }
    catch (e2) { notify(errMsg(e2, 'تعذر الحذف')); setConfirmDel(null); }
  };

  const addDecisionToMeeting = async () => {
    if (!newDecision.text.trim()) return;
    try {
      await axios.post(`${API}/meetings/${detail.id}/decisions`, { text: newDecision.text.trim(), responsible: newDecision.responsible || null, due_date: newDecision.due_date || null, status: 'not_started' }, auth());
      setNewDecision({ text: '', responsible: '', due_date: '' }); notify('تمت إضافة القرار'); loadDetail(detail.id); load();
    } catch (e2) { notify(errMsg(e2, 'فشل إضافة القرار')); }
  };
  const updateDecisionStatus = async (decisionId, status) => {
    try { await axios.patch(`${API}/meetings/${detail.id}/decisions/${decisionId}`, { status }, auth()); notify('تم تحديث حالة القرار'); loadDetail(detail.id); load(); }
    catch (e2) { notify(errMsg(e2, 'فشل تحديث القرار')); }
  };

  const uploadAttachment = async () => {
    if (!attachFile) return;
    if (detail.status !== 'draft' && attachReason.trim().length < 3) { notify('يجب ذكر سبب إضافة المرفق لمحضر معتمد'); return; }
    const fd = new FormData(); fd.append('file', attachFile); if (attachReason) fd.append('reason', attachReason);
    try {
      await axios.post(`${API}/meetings/${detail.id}/attachments`, fd, { headers: { ...auth().headers, 'Content-Type': 'multipart/form-data' } });
      setAttachFile(null); setAttachReason(''); notify('تم رفع المرفق'); loadDetail(detail.id);
    } catch (e2) { notify(errMsg(e2, 'فشل رفع المرفق')); }
  };
  const downloadAttachment = async (a) => {
    try {
      const res = await axios.get(`${API}/meetings/${detail.id}/attachments/${a.id}`, { ...auth(), responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement('a'); link.href = url; link.download = a.filename; document.body.appendChild(link); link.click(); link.remove();
    } catch { notify('فشل تحميل المرفق'); }
  };
  const deleteAttachment = async (a) => {
    try { await axios.delete(`${API}/meetings/${detail.id}/attachments/${a.id}`, auth()); notify('تم حذف المرفق'); loadDetail(detail.id); }
    catch (e2) { notify(errMsg(e2, 'تعذر حذف المرفق')); }
  };

  const studioSettings = studio?.effective || {};
  const studioTemplateUsed = studio?.use_custom ? 'تخصيص مستقل' : null;
  const doExportPdf = async () => {
    if (!printRef.current) return;
    setPdfExporting(true);
    try {
      await exportPdf(printRef.current, `${detail.meeting_number || 'meeting'}`, { orientation: studioSettings.page?.orientation, paperSize: studioSettings.page?.paper_size });
      logExport('meeting_minutes', 'pdf', studioTemplateUsed, 'success', `${detail.meeting_number || 'meeting'}.pdf`);
      notify('تم تصدير PDF');
    } catch { logExport('meeting_minutes', 'pdf', studioTemplateUsed, 'failed', null); notify('فشل تصدير PDF'); }
    finally { setPdfExporting(false); }
  };

  if (selectedId && detail) {
    const decisionsList = detail.decisions || [];
    const decisionsTotal = decisionsList.length;
    const decisionsCompleted = decisionsList.filter((d) => d.status === 'completed').length;
    const decisionsInProgress = decisionsList.filter((d) => d.status === 'in_progress').length;
    const decisionsOverdue = decisionsList.filter((d) => d.is_overdue).length;
    const decisionsPct = decisionsTotal ? Math.round((decisionsCompleted / decisionsTotal) * 100) : 0;
    const decisionsPreview = decisionsList.slice(0, 3);
    return (
      <div className="space-y-4" data-testid="meeting-detail-view">
        <button onClick={() => { setSelectedId(null); setDetail(null); }} className="flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800" data-testid="meeting-back-btn">
          <Icon name="arrowLeft" className="w-4 h-4" /> رجوع إلى القائمة
        </button>
        {toast && <div className="fixed top-16 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-[#16233b] px-4 py-2 text-sm text-white shadow-lg">{toast}</div>}

        <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-xs font-bold text-slate-400" data-testid="meeting-number">{detail.meeting_number}</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${MEETING_STATUS_CLS[detail.status]}`} data-testid="meeting-status-badge">{MEETING_STATUS_LABELS[detail.status]}</span>
                <span className="text-xs text-slate-400">{MEETING_TYPES[detail.meeting_type]}</span>
              </div>
              <h3 className="mt-1.5 text-lg font-bold text-slate-900" data-testid="meeting-detail-title">{detail.title}</h3>
              <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                <span className="flex items-center gap-1"><Icon name="calendar" className="w-3.5 h-3.5" /> {fmtDate(detail.meeting_date)}{detail.meeting_time ? ` — ${detail.meeting_time}` : ''}</span>
                {detail.location && <span className="flex items-center gap-1"><Icon name="building" className="w-3.5 h-3.5" /> {detail.location}</span>}
                {detail.status === 'approved' && <span className="flex items-center gap-1 font-semibold text-teal-700"><Icon name="check" className="w-3.5 h-3.5" /> معتمد من {detail.approved_by} — {fmtDate(detail.approved_at)}</span>}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={doExportPdf} disabled={pdfExporting} className="flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50" data-testid="meeting-export-pdf-btn">{pdfExporting ? <><span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-slate-300 border-t-teal-600" /> جاري التصدير...</> : <><Icon name="download" className="w-4 h-4" /> تصدير PDF</>}</button>
              {canManage && detail.status === 'draft' && (
                <>
                  <button onClick={() => openEdit(detail)} className="flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50" data-testid="meeting-edit-btn"><Icon name="cog" className="w-4 h-4" /> تعديل</button>
                  <button onClick={doApprove} className="flex items-center gap-1 rounded-lg bg-teal-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-teal-700" data-testid="meeting-approve-btn"><Icon name="check" className="w-4 h-4" /> اعتماد المحضر</button>
                  <button onClick={() => setConfirmDel(detail)} className="flex items-center gap-1 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50" data-testid="meeting-delete-btn"><Icon name="trash" className="w-4 h-4" /> حذف</button>
                </>
              )}
              {canManage && detail.status === 'approved' && (
                <>
                  <button onClick={openAmend} className="flex items-center gap-1 rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700 hover:bg-amber-100" data-testid="meeting-amend-btn"><Icon name="warning" className="w-4 h-4" /> تعديل استثنائي</button>
                  <button onClick={doArchive} className="flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50" data-testid="meeting-archive-btn"><Icon name="archive" className="w-4 h-4" /> أرشفة</button>
                </>
              )}
              {canManage && detail.status === 'archived' && (
                <button onClick={openAmend} className="flex items-center gap-1 rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700 hover:bg-amber-100" data-testid="meeting-amend-btn"><Icon name="warning" className="w-4 h-4" /> تعديل استثنائي</button>
              )}
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5" data-testid="meeting-decisions-summary-bar">
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
            <DecisionSummaryStat icon="document" color="slate" label="إجمالي القرارات" value={decisionsTotal} testId="meeting-decisions-summary-total" />
            <DecisionSummaryStat icon="check" color="teal" label="منفذ" value={decisionsCompleted} testId="meeting-decisions-summary-completed" />
            <DecisionSummaryStat icon="clock" color="blue" label="قيد التنفيذ" value={decisionsInProgress} testId="meeting-decisions-summary-inprogress" />
            <DecisionSummaryStat icon="warning" color={decisionsOverdue > 0 ? 'red' : 'slate'} label="متأخر" value={decisionsOverdue} testId="meeting-decisions-summary-overdue" />
          </div>
          <div className="mt-4">
            <div className="mb-1.5 flex items-center justify-between text-xs text-slate-500">
              <span>نسبة الإنجاز</span>
              <span className="font-bold text-slate-900" data-testid="meeting-decisions-summary-pct">{decisionsPct}٪</span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
              <div className="h-full rounded-full bg-teal-500 transition-all" style={{ width: `${decisionsPct}%` }} />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="space-y-4 lg:col-span-2">
            <div className="flex gap-1 overflow-x-auto rounded-xl border border-slate-200 bg-white p-1" data-testid="meeting-detail-tabs">
              {MEETING_DETAIL_TABS.map((t) => (
                <button key={t.key} onClick={() => setActiveTab(t.key)} className={`flex-1 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${activeTab === t.key ? 'bg-[#16233b] text-white' : 'text-slate-600 hover:bg-slate-50'}`} data-testid={`meeting-detail-tab-${t.key}`}>
                  {t.label}
                </button>
              ))}
            </div>

            {activeTab === 'minutes' && (
              <div className="space-y-4" data-testid="meeting-tab-minutes">
                {detail.summary && (
                  <div className="rounded-xl border border-slate-200 bg-white p-4">
                    <p className="mb-2 text-sm font-bold text-slate-900">ملخص الاجتماع / المحضر</p>
                    <p className="whitespace-pre-wrap text-sm text-slate-600" data-testid="meeting-summary">{detail.summary}</p>
                  </div>
                )}
                {detail.agenda?.length > 0 && (
                  <div className="rounded-xl border border-slate-200 bg-white p-4">
                    <p className="mb-2 text-sm font-bold text-slate-900">جدول الأعمال</p>
                    <ol className="list-decimal space-y-1 pr-5 text-sm text-slate-600">{detail.agenda.map((a, i) => <li key={i}>{a}</li>)}</ol>
                  </div>
                )}
                <div className="rounded-xl border border-slate-200 bg-white p-4">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <p className="text-sm font-bold text-slate-900">القرارات ({decisionsTotal})</p>
                    {decisionsTotal > 0 && <button onClick={() => setActiveTab('decisions')} className="whitespace-nowrap text-xs font-semibold text-teal-700 hover:text-teal-800" data-testid="meeting-view-all-decisions-btn">عرض جميع القرارات ←</button>}
                  </div>
                  {decisionsTotal === 0 ? <p className="text-sm text-slate-400">لا توجد قرارات مسجّلة</p> : (
                    <div className="space-y-2">
                      {decisionsPreview.map((d) => (
                        <div key={d.id} className="rounded-lg border border-slate-200 p-2.5 text-sm" data-testid={`meeting-decision-preview-${d.id}`}>
                          <div className="flex items-start justify-between gap-2">
                            <p className="min-w-0 flex-1 truncate text-slate-800">{d.text}</p>
                            <span className={`flex-shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${DECISION_STATUS_CLS[d.status]}`}>{DECISION_STATUS_LABELS[d.status]}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'decisions' && (
              <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="meeting-tab-decisions">
                <p className="mb-2 text-sm font-bold text-slate-900">القرارات ({decisionsTotal})</p>
                <div className="space-y-2" data-testid="meeting-decisions-list">
                  {decisionsList.map((d) => (
                    <div key={d.id} className="rounded-lg border border-slate-200 p-3" data-testid={`meeting-decision-item-${d.id}`}>
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-slate-800">{d.text}</p>
                          <div className="mt-1 flex flex-wrap gap-3 text-xs text-slate-400">
                            {d.responsible && <span>المسؤول: {d.responsible}</span>}
                            {d.due_date && <span>موعد التنفيذ: {fmtDate(d.due_date)}</span>}
                            {d.is_overdue && <span className="font-bold text-red-600" data-testid={`meeting-decision-overdue-${d.id}`}>متأخر</span>}
                          </div>
                        </div>
                        {canManage ? (
                          <select value={d.status} onChange={(e) => updateDecisionStatus(d.id, e.target.value)} className={`rounded-full border-0 px-2 py-1 text-[11px] font-bold ${DECISION_STATUS_CLS[d.status]}`} data-testid={`meeting-decision-status-select-${d.id}`}>
                            {Object.entries(DECISION_STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                          </select>
                        ) : (
                          <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${DECISION_STATUS_CLS[d.status]}`}>{DECISION_STATUS_LABELS[d.status]}</span>
                        )}
                      </div>
                    </div>
                  ))}
                  {decisionsTotal === 0 && <p className="text-sm text-slate-400">لا توجد قرارات مسجّلة</p>}
                </div>
                {canManage && detail.status === 'draft' && (
                  <div className="mt-3 rounded-lg border border-dashed border-slate-300 p-3">
                    <p className="mb-2 text-xs font-semibold text-slate-600">إضافة قرار جديد</p>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-4">
                      <input className={`${inputCls} sm:col-span-2`} placeholder="نص القرار" value={newDecision.text} onChange={(e) => setNewDecision({ ...newDecision, text: e.target.value })} data-testid="meeting-new-decision-text" />
                      <input className={inputCls} placeholder="المسؤول" value={newDecision.responsible} onChange={(e) => setNewDecision({ ...newDecision, responsible: e.target.value })} data-testid="meeting-new-decision-responsible" />
                      <input type="date" className={inputCls} value={newDecision.due_date} onChange={(e) => setNewDecision({ ...newDecision, due_date: e.target.value })} data-testid="meeting-new-decision-due" />
                    </div>
                    <button onClick={addDecisionToMeeting} className="mt-2 rounded-lg bg-[#16233b] px-3 py-1.5 text-xs font-semibold text-white" data-testid="meeting-new-decision-submit">إضافة القرار</button>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'attachments' && (
              <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="meeting-tab-attachments">
                <p className="mb-2 text-sm font-bold text-slate-900">المرفقات ({detail.attachments?.length || 0})</p>
                <div className="space-y-2" data-testid="meeting-attachments-list">
                  {(detail.attachments || []).map((a) => (
                    <div key={a.id} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 p-2 text-sm">
                      <button onClick={() => downloadAttachment(a)} className="flex min-w-0 items-center gap-2 text-slate-700 hover:text-teal-700" data-testid={`meeting-attachment-download-${a.id}`}><Icon name="clipboard" className="w-4 h-4 flex-shrink-0" /> <span className="truncate">{a.filename}</span></button>
                      {canManage && detail.status === 'draft' && <button onClick={() => deleteAttachment(a)} className="flex-shrink-0 text-slate-400 hover:text-red-600" data-testid={`meeting-attachment-delete-${a.id}`}><Icon name="trash" className="w-4 h-4" /></button>}
                    </div>
                  ))}
                  {(!detail.attachments || detail.attachments.length === 0) && <p className="text-sm text-slate-400">لا توجد مرفقات</p>}
                </div>
                {canManage && (
                  <div className="mt-3 rounded-lg border border-dashed border-slate-300 p-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <input type="file" onChange={(e) => setAttachFile(e.target.files?.[0] || null)} className="text-xs" data-testid="meeting-attachment-file-input" />
                      {detail.status !== 'draft' && <input className={`${inputCls} max-w-xs`} placeholder="سبب إضافة المرفق (إلزامي لمحضر معتمد)" value={attachReason} onChange={(e) => setAttachReason(e.target.value)} data-testid="meeting-attachment-reason" />}
                      <button onClick={uploadAttachment} className="rounded-lg bg-[#16233b] px-3 py-1.5 text-xs font-semibold text-white" data-testid="meeting-attachment-upload-btn">رفع المرفق</button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'activity' && (
              <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="meeting-tab-activity">
                <p className="mb-2 text-sm font-bold text-slate-900">سجل النشاط</p>
                {activityLoading ? (
                  <div className="space-y-2">{[1, 2, 3].map((i) => <div key={i} className="h-10 animate-pulse rounded-lg bg-slate-100" />)}</div>
                ) : meetingActivity.length === 0 ? (
                  <p className="text-sm text-slate-400">لا يوجد نشاط مسجّل لهذا الاجتماع</p>
                ) : (
                  <ul className="space-y-2" data-testid="meeting-activity-list">
                    {meetingActivity.map((a, idx) => (
                      <li key={idx} className="rounded-lg border border-slate-100 p-2.5 text-sm" data-testid="meeting-activity-item">
                        <p className="font-semibold text-slate-800">{a.action}</p>
                        <p className="text-xs text-slate-500">{a.details}</p>
                        <p className="mt-1 text-[11px] text-slate-400">{a.user_name || '—'} — {fmtDate(a.created_at)}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

          <div className="space-y-4 lg:col-span-1">
            {(detail.attendees?.length > 0 || detail.apologies?.length > 0) && (
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <p className="mb-3 flex items-center gap-2 text-sm font-bold text-slate-900"><Icon name="users" className="w-4 h-4 text-slate-500" /> الحضور</p>
                {detail.attendees?.length > 0 && (
                  <div className="mb-3">
                    <p className="mb-1 text-[11px] font-semibold text-slate-400">الحاضرون ({detail.attendees.length})</p>
                    <ul className="space-y-1" data-testid="meeting-attendees-list">
                      {detail.attendees.map((name, i) => <li key={i} className="border-b border-slate-50 py-1.5 text-sm text-slate-700 last:border-0">{name}</li>)}
                    </ul>
                  </div>
                )}
                {detail.apologies?.length > 0 && (
                  <div>
                    <p className="mb-1 text-[11px] font-semibold text-slate-400">المعتذرون ({detail.apologies.length})</p>
                    <ul className="space-y-1" data-testid="meeting-apologies-list">
                      {detail.apologies.map((name, i) => <li key={i} className="border-b border-slate-50 py-1.5 text-sm text-slate-500 last:border-0">{name}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {(detail.previous_meeting || detail.next_meeting) && (
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <p className="mb-3 text-sm font-bold text-slate-900">اجتماعات مرتبطة</p>
                <div className="space-y-2">
                  {detail.previous_meeting && (
                    <button onClick={() => setSelectedId(detail.previous_meeting.id)} className="block w-full rounded-lg border border-slate-200 p-2.5 text-right hover:border-teal-300 hover:bg-teal-50/30" data-testid="meeting-link-previous">
                      <span className="block text-[10px] text-slate-400">← الاجتماع السابق</span>
                      <span className="block text-xs font-semibold text-slate-700">{detail.previous_meeting.meeting_number} — {detail.previous_meeting.title}</span>
                    </button>
                  )}
                  {detail.next_meeting && (
                    <button onClick={() => setSelectedId(detail.next_meeting.id)} className="block w-full rounded-lg border border-slate-200 p-2.5 text-right hover:border-teal-300 hover:bg-teal-50/30" data-testid="meeting-link-next">
                      <span className="block text-[10px] text-slate-400">الاجتماع اللاحق →</span>
                      <span className="block text-xs font-semibold text-slate-700">{detail.next_meeting.meeting_number} — {detail.next_meeting.title}</span>
                    </button>
                  )}
                </div>
              </div>
            )}

            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="mb-2 flex items-center justify-between gap-2">
                <p className="text-sm font-bold text-slate-900">المرفقات ({detail.attachments?.length || 0})</p>
                <button onClick={() => setActiveTab('attachments')} className="whitespace-nowrap text-xs font-semibold text-teal-700 hover:text-teal-800" data-testid="meeting-view-attachments-btn">إدارة الكل</button>
              </div>
              {(!detail.attachments || detail.attachments.length === 0) ? <p className="text-sm text-slate-400">لا توجد مرفقات</p> : (
                <ul className="space-y-1.5" data-testid="meeting-attachments-preview-list">
                  {detail.attachments.slice(0, 4).map((a) => (
                    <li key={a.id}>
                      <button onClick={() => downloadAttachment(a)} className="flex w-full items-center gap-2 text-xs text-slate-600 hover:text-teal-700">
                        <Icon name="clipboard" className="w-3.5 h-3.5 flex-shrink-0" /> <span className="min-w-0 truncate">{a.filename}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        <div style={{ position: 'fixed', top: 0, left: '-99999px' }} ref={printRef}>
          <div style={{ width: '700px', background: '#fff' }}>
            <ReportPrintFrame settings={studioSettings} identity={identity} reportTitle={`محضر اجتماع — ${detail.meeting_number}`} exportedByName={getUserName()}>
              <div>
                <h3 style={{ fontSize: '16px', fontWeight: 'bold', marginTop: 0 }}>{detail.title}</h3>
                <p style={{ fontSize: '12px', color: '#333' }}>النوع: {MEETING_TYPES[detail.meeting_type]}</p>
                <p style={{ fontSize: '12px', color: '#333' }}>التاريخ: {fmtDate(detail.meeting_date)} {detail.meeting_time ? `— ${detail.meeting_time}` : ''} {detail.location ? `— ${detail.location}` : ''}</p>
                <p style={{ fontSize: '12px', color: '#333' }}>الحالة: {MEETING_STATUS_LABELS[detail.status]}{detail.status === 'approved' ? ` — معتمد من ${detail.approved_by} بتاريخ ${fmtDate(detail.approved_at)}` : ''}</p>
                {detail.attendees?.length > 0 && <p style={{ fontSize: '12px', marginTop: '8px' }}><b>الحاضرون:</b> {detail.attendees.join('، ')}</p>}
                {detail.apologies?.length > 0 && <p style={{ fontSize: '12px' }}><b>المعتذرون:</b> {detail.apologies.join('، ')}</p>}
                {detail.agenda?.length > 0 && (
                  <div style={{ marginTop: '10px' }}>
                    <b style={{ fontSize: '13px' }}>جدول الأعمال</b>
                    <ol style={{ fontSize: '12px', paddingRight: '18px' }}>{detail.agenda.map((a, i) => <li key={i}>{a}</li>)}</ol>
                  </div>
                )}
                {detail.summary && (
                  <div style={{ marginTop: '10px' }}>
                    <b style={{ fontSize: '13px' }}>ملخص الاجتماع</b>
                    <p style={{ fontSize: '12px', whiteSpace: 'pre-wrap' }}>{detail.summary}</p>
                  </div>
                )}
                {detail.decisions?.length > 0 && (
                  <div style={{ marginTop: '10px' }}>
                    <b style={{ fontSize: '13px' }}>القرارات</b>
                    <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse', marginTop: '4px' }}>
                      <thead><tr>
                        <th style={{ border: '1px solid #ccc', padding: '5px 6px', verticalAlign: 'middle', lineHeight: 1.6 }}>القرار</th>
                        <th style={{ border: '1px solid #ccc', padding: '5px 6px', verticalAlign: 'middle', lineHeight: 1.6 }}>المسؤول</th>
                        <th style={{ border: '1px solid #ccc', padding: '5px 6px', verticalAlign: 'middle', lineHeight: 1.6 }}>موعد التنفيذ</th>
                        <th style={{ border: '1px solid #ccc', padding: '5px 6px', verticalAlign: 'middle', lineHeight: 1.6 }}>الحالة</th>
                      </tr></thead>
                      <tbody>
                        {detail.decisions.map((d) => (
                          <tr key={d.id}>
                            <td style={{ border: '1px solid #ccc', padding: '5px 6px', verticalAlign: 'middle', lineHeight: 1.6 }}>{d.text}</td>
                            <td style={{ border: '1px solid #ccc', padding: '5px 6px', verticalAlign: 'middle', lineHeight: 1.6 }}>{d.responsible || '—'}</td>
                            <td style={{ border: '1px solid #ccc', padding: '5px 6px', verticalAlign: 'middle', lineHeight: 1.6 }}>{d.due_date ? fmtDate(d.due_date) : '—'}</td>
                            <td style={{ border: '1px solid #ccc', padding: '5px 6px', verticalAlign: 'middle', lineHeight: 1.6 }}>{DECISION_STATUS_LABELS[d.status]}{d.is_overdue ? ' (متأخر)' : ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </ReportPrintFrame>
          </div>
        </div>

        <Modal open={formModal} title="تعديل الاجتماع" onClose={() => setFormModal(false)} wide>
          {err && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="meeting-form-error">{err}</div>}
          <form onSubmit={submitForm} className="space-y-3">
            <MeetingFields form={form} setForm={setForm} meetings={list} excludeId={editing?.id} />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setFormModal(false)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
              <PrimaryBtn type="submit" data-testid="meeting-form-submit">حفظ</PrimaryBtn>
            </div>
          </form>
        </Modal>

        <Modal open={amendModal} title="تعديل استثنائي على محضر معتمد" onClose={() => setAmendModal(false)} wide>
          {err && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="meeting-amend-error">{err}</div>}
          <div className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">هذا التعديل سيُسجَّل في سجل النشاط مع القيم القديمة والجديدة واسمك والتاريخ.</div>
          <form onSubmit={submitAmend} className="space-y-3">
            <Field label="سبب التعديل" required><input className={inputCls} value={amendReason} onChange={(e) => setAmendReason(e.target.value)} placeholder="مثال: تصحيح خطأ إملائي في الاسم" data-testid="meeting-amend-reason" /></Field>
            <MeetingFields form={amendForm} setForm={setAmendForm} meetings={list} excludeId={detail.id} />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setAmendModal(false)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
              <PrimaryBtn type="submit" data-testid="meeting-amend-submit">حفظ التعديل الاستثنائي</PrimaryBtn>
            </div>
          </form>
        </Modal>

        <Modal open={Boolean(confirmDel)} title="حذف المسودة" onClose={() => setConfirmDel(null)}>
          <p className="text-sm text-slate-600">حذف مسودة اجتماع «{confirmDel?.title}»؟</p>
          <div className="mt-4 flex justify-end gap-2">
            <button onClick={() => setConfirmDel(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
            <button onClick={doDelete} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white" data-testid="meeting-confirm-delete">حذف</button>
          </div>
        </Modal>
        {detailLoading && <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/10"><div className="rounded-lg bg-white px-4 py-2 text-sm shadow">جارٍ التحميل...</div></div>}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="meetings-list-view">
      {toast && <div className="fixed top-16 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-[#16233b] px-4 py-2 text-sm text-white shadow-lg">{toast}</div>}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-slate-900">الاجتماعات والقرارات</h2>
          <p className="text-sm text-slate-500">محاضر الاجتماعات، جداول الأعمال، والقرارات ومتابعة تنفيذها</p>
        </div>
        {canManage && <PrimaryBtn onClick={openCreate} data-testid="meeting-add-btn"><Icon name="plus" className="w-4 h-4" /> اجتماع جديد</PrimaryBtn>}
      </div>

      <div className="flex flex-wrap gap-2 rounded-xl border border-slate-200 bg-white p-3" data-testid="meeting-filters">
        <input className={`${inputCls} max-w-xs`} placeholder="بحث بالعنوان أو رقم الاجتماع" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} data-testid="meeting-filter-search" />
        <select className={`${inputCls} max-w-[160px]`} value={filters.meeting_type} onChange={(e) => setFilters({ ...filters, meeting_type: e.target.value })} data-testid="meeting-filter-type">
          <option value="">كل الأنواع</option>
          {Object.entries(MEETING_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select className={`${inputCls} max-w-[140px]`} value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} data-testid="meeting-filter-status">
          <option value="">كل الحالات</option>
          {Object.entries(MEETING_STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <input type="date" className={`${inputCls} max-w-[150px]`} value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} data-testid="meeting-filter-date-from" />
        <input type="date" className={`${inputCls} max-w-[150px]`} value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} data-testid="meeting-filter-date-to" />
      </div>

      {loading ? <div className="space-y-2">{[1, 2, 3].map((i) => <div key={i} className="h-16 animate-pulse rounded-xl bg-slate-100" />)}</div>
        : list.length === 0 ? <div className="rounded-xl border border-slate-200 bg-white p-8"><EmptyState icon="calendar" text="لا توجد اجتماعات مسجّلة بعد" testId="meeting-empty" /></div>
        : (
          <div className="space-y-2">
            {list.map((m) => (
              <button key={m.id} onClick={() => setSelectedId(m.id)} className="block w-full rounded-xl border border-slate-200 bg-white p-4 text-right hover:border-teal-300 hover:bg-teal-50/30" data-testid={`meeting-row-${m.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xs font-bold text-slate-400">{m.meeting_number}</span>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${MEETING_STATUS_CLS[m.status]}`}>{MEETING_STATUS_LABELS[m.status]}</span>
                      <span className="text-xs text-slate-400">{MEETING_TYPES[m.meeting_type]}</span>
                    </div>
                    <p className="mt-1 text-sm font-bold text-slate-900">{m.title}</p>
                    <p className="text-xs text-slate-500">{fmtDate(m.meeting_date)}{m.meeting_time ? ` — ${m.meeting_time}` : ''}{m.location ? ` — ${m.location}` : ''}</p>
                  </div>
                  <div className="text-xs text-slate-500">{m.decisions_completed}/{m.decisions_count} قرارات منفذة</div>
                </div>
              </button>
            ))}
          </div>
        )}

      <Modal open={formModal} title={editing ? 'تعديل الاجتماع' : 'اجتماع جديد'} onClose={() => setFormModal(false)} wide>
        {err && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="meeting-form-error">{err}</div>}
        <form onSubmit={submitForm} className="space-y-3">
          <MeetingFields form={form} setForm={setForm} meetings={list} excludeId={editing?.id} />
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setFormModal(false)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
            <PrimaryBtn type="submit" data-testid="meeting-form-submit">حفظ</PrimaryBtn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
